// src/api/apiService.js

// 您的后端服务地址
const API_BASE_URL = 'https://subapi.eoe.ink';
// 本地调试地址
// const API_BASE_URL = 'http://localhost:10888';

// ESA 解析服务地址。
const BILI_PARSE_API_URL = 'https://api.eoe.fan/bili/parse';

/**
 * 获取解密后的 SECRET_KEY
 * 从 localStorage 中获取登录时解密的密钥
 */
const getSecretKey = () => {
  const secretKey = localStorage.getItem('secretKey');
  if (!secretKey) {
    console.warn('未找到 SECRET_KEY，请先登录');
    return '';
  }
  return secretKey;
};

/**
 * 这是一个辅助函数，用于统一处理API请求。
 * 它会自动处理错误，并为需要授权的请求添加密钥。
 * @param {string} url - 请求的API地址
 * @param {object} options - fetch函数的配置选项
 * @returns {Promise<any>}
 */
const apiRequest = async (url, options = {}) => {
  if (options.auth) {
    const secretKey = getSecretKey();
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${secretKey}`,
      // 默认是 json，但如果 options 提供了，就用 options 里的
      'Content-Type': options.headers?.['Content-Type'] || 'application/json',
    };
  }
  delete options.auth;

  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      // 尝试解析错误，如果失败则返回文本
      let errorMsg;
      try {
        const errorData = await response.json();
        errorMsg = errorData.message || JSON.stringify(errorData);
      } catch (e) {
        errorMsg = await response.text();
      }
      throw new Error(errorMsg || `请求失败，状态码: ${response.status}`);
    }
    if (response.status === 204) { // No Content, like DELETE
      return { success: true };
    }
    // POST 字幕成功后，后端可能返回空内容或成功信息
    if (response.headers.get('content-length') === '0') {
      return { success: true };
    }
    return response.json(); // 默认还是解析 JSON
  } catch (error) {
    console.error('API请求出错:', error);
    throw error;
  }
};

const adminRequest = (path, options = {}) => apiRequest(`${API_BASE_URL}/admin${path}`, {
  ...options,
  auth: true,
});

/**
 * 辅助函数，用于请求纯文本响应（如 SRT 字幕）
 */
const apiRequestText = async (url, options = {}) => {
  if (options.auth) {
    const secretKey = getSecretKey();
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${secretKey}`,
    };
  }
  delete options.auth;

  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      let errorMsg;
      try {
        const errorData = await response.json();
        errorMsg = errorData.message || JSON.stringify(errorData);
      } catch (e) {
        errorMsg = await response.text();
      }
      throw new Error(errorMsg || `请求失败，状态码: ${response.status}`);
    }
    return response.text();
  } catch (error) {
    console.error('API请求出错:', error);
    throw error;
  }
};

const adminRequestText = (path, options = {}) => apiRequestText(`${API_BASE_URL}/admin${path}`, {
  ...options,
  auth: true,
});

// ======================================================================
// =================== 通知函数 ===================
// ======================================================================

// 获取所有通知列表 (GET)
export const fetchNotifications = () => apiRequest(`${API_BASE_URL}/notifications`);

// 创建新通知 (POST)
export const createNotification = (notification) => apiRequest(`${API_BASE_URL}/notifications`, {
  method: 'POST',
  body: JSON.stringify(notification),
  auth: true,
});

// ======================================================================
// =================== 社团函数 ===================
// ======================================================================

// 获取所有社团列表 (GET)
export const fetchOrganizations = () => adminRequest('/organizations');

// 根据ID获取单个社团 (GET)
export const fetchOrganizationById = (id) => adminRequest(`/organizations/${id}`);

// 创建新社团 (POST)
export const createOrganization = (organization) => apiRequest(`${API_BASE_URL}/organizations`, {
  method: 'POST',
  body: JSON.stringify(organization), // 将JS对象转换为JSON字符串
  auth: true, // 标记此请求需要授权
});

// 更新社团信息 (PUT)
export const updateOrganization = (id, organization) => apiRequest(`${API_BASE_URL}/organizations/${id}`, {
  method: 'PUT',
  body: JSON.stringify(organization),
  auth: true,
});

// 删除社团 (DELETE)
export const deleteOrganization = (id) => apiRequest(`${API_BASE_URL}/organizations/${id}`, {
  method: 'DELETE',
  auth: true,
});

// 根据社团ID获取作者列表 (GET)
export const fetchAuthorsByOrganization = (orgId) => {
  if (!orgId) return Promise.resolve([]);
  return adminRequest(`/organizations/${orgId}/authors`);
};

// ======================================================================
// =================== 作者函数 ===================
// ======================================================================

// 获取所有作者列表 (GET)
export const fetchAuthors = () => adminRequest('/authors');

// 根据ID获取单个作者 (GET)
export const fetchAuthorById = (id) => adminRequest(`/authors/${id}`);

// 创建新作者 (POST)
export const createAuthor = (author) => apiRequest(`${API_BASE_URL}/authors`, {
  method: 'POST',
  body: JSON.stringify(author),
  auth: true,
});

// 更新作者信息 (PUT)
export const updateAuthor = (id, author) => apiRequest(`${API_BASE_URL}/authors/${id}`, {
  method: 'PUT',
  body: JSON.stringify(author),
  auth: true,
});

// 删除作者 (DELETE)
export const deleteAuthor = (id) => apiRequest(`${API_BASE_URL}/authors/${id}`, {
  method: 'DELETE',
  auth: true,
});

// ======================================================================
// =================== Clips 函数 ===================
// ======================================================================

/**
 * 根据社团ID获取Clips列表（支持分页和筛选）
 * PostgreSQL 版本返回分页数据格式 { clips, total, page, size }
 * @param {number} orgId - 社团ID
 * @param {object} options - 可选参数 { keyword, live_format, live_content, start_date, end_date, page, size }
 * @returns {Promise<{ clips: Array, total: number, page: number, size: number }>}
 */
export const fetchClipsByOrganization = (orgId, options = {}) => {
  if (!orgId) {
    // 如果没有提供社团ID，直接返回一个空的 resolved Promise，避免不必要的请求
    return Promise.resolve({ clips: [], total: 0, page: 1, size: 20 });
  }
  
  // 构建查询参数
  const params = new URLSearchParams();
  if (options.keyword) params.append('keyword', options.keyword);
  if (options.live_format) params.append('live_format', options.live_format);
  if (options.live_content) params.append('live_content', options.live_content);
  if (options.start_date) params.append('start_date', options.start_date);
  if (options.end_date) params.append('end_date', options.end_date);
  if (options.page) params.append('page', options.page);
  if (options.size) params.append('size', options.size);
  
  const queryString = params.toString();
  const url = `/organizations/${orgId}/clips${queryString ? `?${queryString}` : ''}`;
  
  return adminRequest(url);
};

export const fetchCalendarClips = (options = {}) => {
  const params = new URLSearchParams();
  if (options.organizationId) params.append('organizationId', options.organizationId);
  if (options.keyword) params.append('keyword', options.keyword);
  if (options.live_format) params.append('live_format', options.live_format);
  if (options.live_content) params.append('live_content', options.live_content);
  if (options.start_date) params.append('start_date', options.start_date);
  if (options.end_date) params.append('end_date', options.end_date);

  const queryString = params.toString();
  return apiRequest(`${API_BASE_URL}/calendar/clips${queryString ? `?${queryString}` : ''}`);
};

/**
 * 根据作者ID获取Clips列表（支持分页和类型筛选）
 * @param {number} authorId - 作者ID
 * @param {object} options - 可选参数 { type, page, size }
 * @returns {Promise<Array>}
 */
export const fetchClipsByAuthor = (authorId, options = {}) => {
  if (!authorId) {
    return Promise.resolve([]);
  }
  
  const params = new URLSearchParams();
  if (options.type !== undefined) params.append('type', options.type);
  if (options.page) params.append('page', options.page);
  if (options.size) params.append('size', options.size);
  
  const queryString = params.toString();
  const url = `${API_BASE_URL}/authors/${authorId}/clips${queryString ? `?${queryString}` : ''}`;
  
  return apiRequest(url);
};

/**
 * 根据ID获取单个Clip (GET)
 * @param {number} id - Clip ID
 * @returns {Promise<object>}
 */
export const fetchClipById = (id) => adminRequest(`/clips/${id}`);

/**
 * 根据BV号获取Clip (GET)
 * @param {string} bv - B站视频BV号
 * @returns {Promise<object>}
 */
export const fetchClipByBv = (bv) => {
  if (!bv) return Promise.resolve(null);
  return apiRequest(`${API_BASE_URL}/clips?bv=${bv}`);
};

// 创建新Clip (POST)
export const createClip = (clipData) => apiRequest(`${API_BASE_URL}/clips`, {
  method: 'POST',
  body: JSON.stringify(clipData),
  auth: true,
});

// 删除Clip信息 (DELETE)
export const deleteClip = (id) => apiRequest(`${API_BASE_URL}/clips/${id}`, {
  method: 'DELETE',
  auth: true,
});

// 更新Clip信息 (PUT)
export const updateClip = (id, clipData) => apiRequest(`${API_BASE_URL}/clips/${id}`, {
  method: 'PUT',
  body: JSON.stringify(clipData),
  auth: true,
});

/**
 * 获取Clip片段（用于切片）
 * @param {number} clipId - Clip ID
 * @param {object} options - { startTime, endTime, audio } 时间格式: "HH:MM:SS.mmm"
 * @returns {Promise<object>}
 */
export const fetchClipSegment = (clipId, options = {}) => {
  const params = new URLSearchParams();
  if (options.startTime) params.append('startTime', options.startTime);
  if (options.endTime) params.append('endTime', options.endTime);
  if (options.audio !== undefined) params.append('audio', options.audio);
  
  const queryString = params.toString();
  return apiRequest(`${API_BASE_URL}/clips/${clipId}/segment?${queryString}`);
};

export const fetchBiliVideoParts = async (bvid) => {
  const fetchPart = async (part) => {
    try {
      const params = new URLSearchParams({ bv: bvid, p: String(part) });
      const data = await apiRequest(`${BILI_PARSE_API_URL}?${params}`);
      if (!Array.isArray(data) || data.length !== 1 || data[0]?.part !== part) {
        throw new Error('解析服务返回的分 P 数据不正确');
      }
      return data[0];
    } catch (error) {
      throw new Error(`获取 P${part} 失败：${error.message}`);
    }
  };

  const first = await fetchPart(1);
  const totalParts = first.totalParts;
  if (!Number.isSafeInteger(totalParts) || totalParts < 1) {
    throw new Error('解析服务未返回有效的 totalParts，请部署最新版 ESA 函数');
  }
  const parts = [first];
  for (let part = 2; part <= totalParts; part += 1) {
    const item = await fetchPart(part);
    if (item.totalParts !== totalParts) {
      throw new Error('视频分 P 数量发生变化，请重新解析');
    }
    parts.push(item);
  }
  return parts;
};

// ======================================================================
// =================== 字幕函数 ===================
// ======================================================================

/**
 * 获取Clip的字幕列表（JSON格式）
 * @param {number} clipId - Clip ID
 * @param {string} keyword - 可选，搜索关键词（支持同音字搜索）
 * @returns {Promise<Array>} - 字幕数组 [{ clipId, lineId, start, end, markedContent, len, matchMode? }]
 */
export const fetchSubtitleForClip = (clipId, keyword = '') => {
  let url = `/clips/${clipId}/subtitles`;
  if (keyword) {
    url += `?keyword=${encodeURIComponent(keyword)}`;
  }
  return adminRequest(url);
};

/**
 * 获取Clip的字幕（SRT纯文本格式）
 * @param {number} clipId - Clip ID
 * @returns {Promise<string>} - SRT格式字幕文本
 */
export const fetchSubtitleSrtForClip = (clipId) => {
  return adminRequestText(`/clips/${clipId}/srt`);
};

/**
 * 上传字幕（POST，会覆盖现有字幕）
 * @param {number} clipId - Clip ID
 * @param {string} srtContent - SRT格式字幕内容
 * @returns {Promise<object>}
 */
export const uploadSubtitle = (clipId, srtContent) => {
  return apiRequest(`${API_BASE_URL}/clips/${clipId}/subtitles`, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain', // 明确指定 Content-Type
    },
    body: srtContent, // body 直接是字符串
    auth: true,
  });
};

/**
 * 更新字幕偏移量
 * @param {number} clipId - Clip ID
 * @param {number} offset - 偏移量（微秒）
 * @returns {Promise<object>}
 */
export const updateSubtitleOffset = (clipId, offset) => {
  return apiRequest(`${API_BASE_URL}/clips/${clipId}/subtitles`, {
    method: 'PUT',
    body: JSON.stringify({ offset }),
    auth: true,
  });
};

/**
 * 更新单行字幕
 * @param {number} clipId - Clip ID
 * @param {number} lineId - 字幕行ID
 * @param {object} subtitleData - { start?, end?, content? }
 * @returns {Promise<object>}
 */
export const updateSubtitleLine = (clipId, lineId, subtitleData) => {
  return apiRequest(`${API_BASE_URL}/clips/${clipId}/subtitles/${lineId}`, {
    method: 'PUT',
    body: JSON.stringify(subtitleData),
    auth: true,
  });
};

/**
 * 删除Clip的所有字幕
 * @param {number} clipId - Clip ID
 * @returns {Promise<object>}
 */
export const deleteSubtitle = (clipId) => {
  return apiRequest(`${API_BASE_URL}/clips/${clipId}/subtitles`, {
    method: 'DELETE',
    auth: true,
  });
};

// ======================================================================
// =================== AI 总结函数 ===================
// ======================================================================

/**
 * 获取 Clip 的 AI 总结
 * @param {number} clipId - Clip ID
 * @returns {Promise<object>} - { id, clipId, topics, keywords, overallSummary, model, createdAt, updatedAt }
 */
export const fetchSummaryForClip = (clipId) => {
  return adminRequest(`/clips/${clipId}/summary`);
};

/**
 * 检查 Clip 是否有 AI 总结
 * @param {number} clipId - Clip ID
 * @returns {Promise<{ exists: boolean }>}
 */
export const checkSummaryExists = (clipId) => {
  return adminRequest(`/clips/${clipId}/summary/exists`);
};

/**
 * 获取 AI 总结生成状态
 * @param {number} clipId - Clip ID
 * @returns {Promise<{ status: 'processing' | 'completed' | 'not_started', startedAt?: string }>}
 */
export const fetchSummaryStatus = (clipId) => {
  return adminRequest(`/clips/${clipId}/summary/status`);
};

/**
 * 生成 AI 总结（需认证）
 * 注意：深度思考模型生成时间较长（1-5分钟），可能返回 processing 状态
 * @param {number} clipId - Clip ID
 * @returns {Promise<object>} - { status: 'success' | 'processing', ... }
 */
export const generateSummary = (clipId) => {
  return apiRequest(`${API_BASE_URL}/clips/${clipId}/summary`, {
    method: 'POST',
    auth: true,
  });
};

/**
 * 更新 AI 总结（需认证）
 * @param {number} clipId - Clip ID
 * @param {object} summaryData - { topics?, keywords?, overallSummary? }
 * @returns {Promise<object>}
 */
export const updateSummary = (clipId, summaryData) => {
  return apiRequest(`${API_BASE_URL}/clips/${clipId}/summary`, {
    method: 'PUT',
    body: JSON.stringify(summaryData),
    auth: true,
  });
};

/**
 * 删除 AI 总结（需认证）
 * @param {number} clipId - Clip ID
 * @returns {Promise<object>}
 */
export const deleteSummary = (clipId) => {
  return apiRequest(`${API_BASE_URL}/clips/${clipId}/summary`, {
    method: 'DELETE',
    auth: true,
  });
};

/**
 * 根据关键词搜索 AI 总结
 * @param {string} keyword - 搜索关键词
 * @param {number} limit - 返回数量限制
 * @param {number} offset - 偏移量
 * @returns {Promise<object>} - { keyword, count, results }
 */
export const searchSummaries = (keyword, limit = 20, offset = 0) => {
  const params = new URLSearchParams();
  params.append('keyword', keyword);
  params.append('limit', limit);
  params.append('offset', offset);
  return apiRequest(`${API_BASE_URL}/summaries/search?${params.toString()}`);
};

/**
 * 获取 AI 总结统计信息（需认证，仅超管）
 * @returns {Promise<object>} - { totalCount, totalPromptTokens, totalCompletionTokens, totalReasoningTokens }
 */
export const fetchSummaryStats = () => {
  return apiRequest(`${API_BASE_URL}/summaries/stats`, {
    auth: true,
  });
};

// ======================================================================
// =================== AI Key 账号管理函数 ===================
// ======================================================================

/**
 * 获取 AI Key 账号列表（需认证，仅超管）
 * @returns {Promise<Array>} - 账号对象数组
 */
export const fetchAIAccounts = () => {
  return apiRequest(`${API_BASE_URL}/admin/ai/accounts`, {
    auth: true,
  });
};

/**
 * 创建 AI Key 账号（需认证，仅超管）
 * @param {object} payload - { provider, name, scene, baseUrl, apiKey, model, priority, weight, remark }
 * @returns {Promise<object>} - 新建账号对象
 */
export const createAIAccount = (payload) => {
  return apiRequest(`${API_BASE_URL}/admin/ai/accounts`, {
    method: 'POST',
    body: JSON.stringify(payload),
    auth: true,
  });
};

/**
 * 更新 AI Key 账号（需认证，仅超管）
 * @param {number} id - 账号 ID
 * @param {object} payload - 同创建，apiKey 为空字符串表示不更换
 * @returns {Promise<object>} - 更新后账号对象
 */
export const updateAIAccount = (id, payload) => {
  return apiRequest(`${API_BASE_URL}/admin/ai/accounts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
    auth: true,
  });
};

/**
 * 删除 AI Key 账号（需认证，仅超管）
 * @param {number} id - 账号 ID
 * @returns {Promise<{ success: true }>}
 */
export const deleteAIAccount = (id) => {
  return apiRequest(`${API_BASE_URL}/admin/ai/accounts/${id}`, {
    method: 'DELETE',
    auth: true,
  });
};

/**
 * 启用 AI Key 账号（需认证，仅超管）
 * @param {number} id - 账号 ID
 * @returns {Promise<{ status: 'success', id: number }>}
 */
export const enableAIAccount = (id) => {
  return apiRequest(`${API_BASE_URL}/admin/ai/accounts/${id}/enable`, {
    method: 'POST',
    auth: true,
  });
};

/**
 * 停用 AI Key 账号（需认证，仅超管）
 * @param {number} id - 账号 ID
 * @returns {Promise<{ status: 'success', id: number }>}
 */
export const disableAIAccount = (id) => {
  return apiRequest(`${API_BASE_URL}/admin/ai/accounts/${id}/disable`, {
    method: 'POST',
    auth: true,
  });
};

/**
 * 重置 AI Key 账号状态（需认证，仅超管）
 * 效果：status=active，清空冷却/错误/失败计数，立即刷新账号池
 * @param {number} id - 账号 ID
 * @returns {Promise<{ status: 'success', id: number }>}
 */
export const resetAIAccountStatus = (id) => {
  return apiRequest(`${API_BASE_URL}/admin/ai/accounts/${id}/reset-status`, {
    method: 'POST',
    auth: true,
  });
};

/**
 * 获取 AI Key 账号调用日志（需认证，仅超管）
 * @param {number} id - 账号 ID
 * @param {object} options - { limit, offset }
 * @returns {Promise<Array>} - 日志数组
 */
export const fetchAIAccountLogs = (id, options = {}) => {
  const params = new URLSearchParams();
  if (options.limit !== undefined) params.append('limit', options.limit);
  if (options.offset !== undefined) params.append('offset', options.offset);
  const query = params.toString();
  return apiRequest(`${API_BASE_URL}/admin/ai/accounts/${id}/logs${query ? `?${query}` : ''}`, {
    auth: true,
  });
};

/**
 * 获取 AI Key 账号聚合统计（需认证，仅超管）
 * @returns {Promise<Array>} - 每个账号的统计对象数组
 */
export const fetchAIAccountStats = () => {
  return apiRequest(`${API_BASE_URL}/admin/ai/accounts/stats`, {
    auth: true,
  });
};

// ======================================================================
// =================== 桌面版本管理函数 ===================
// ======================================================================

/**
 * 获取桌面版本发布记录列表（需认证，仅超管）
 * @param {object} options - { channel, platform, arch, isActive }
 * @returns {Promise<Array>}
 */
export const fetchDesktopReleases = (options = {}) => {
  const params = new URLSearchParams();
  if (options.channel) params.append('channel', options.channel);
  if (options.platform) params.append('platform', options.platform);
  if (options.arch) params.append('arch', options.arch);
  if (options.isActive !== undefined && options.isActive !== '') params.append('isActive', options.isActive);
  const query = params.toString();
  return apiRequest(`${API_BASE_URL}/admin/desktop/releases${query ? `?${query}` : ''}`, {
    auth: true,
  });
};

/**
 * 创建桌面版本发布记录（需认证，仅超管）
 * @param {object} payload - 完整版本对象
 * @returns {Promise<object>} - 创建后的完整版本对象
 */
export const createDesktopRelease = (payload) => {
  return apiRequest(`${API_BASE_URL}/admin/desktop/releases`, {
    method: 'POST',
    body: JSON.stringify(payload),
    auth: true,
  });
};

/**
 * 更新桌面版本发布记录（需认证，仅超管）
 * 注意：为全量更新，需传入完整对象
 * @param {number} id - 记录 ID
 * @param {object} payload - 完整版本对象
 * @returns {Promise<object>} - 更新后的完整版本对象
 */
export const updateDesktopRelease = (id, payload) => {
  return apiRequest(`${API_BASE_URL}/admin/desktop/releases/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
    auth: true,
  });
};

/**
 * 删除桌面版本发布记录（需认证，仅超管）
 * 建议仅用于误录入/脏数据清理
 * @param {number} id - 记录 ID
 * @returns {Promise<{ status: 'success', id: number }>}
 */
export const deleteDesktopRelease = (id) => {
  return apiRequest(`${API_BASE_URL}/admin/desktop/releases/${id}`, {
    method: 'DELETE',
    auth: true,
  });
};

/**
 * 启用桌面版本（需认证，仅超管）
 * @param {number} id - 记录 ID
 * @returns {Promise<{ status: 'success', id: number }>}
 */
export const enableDesktopRelease = (id) => {
  return apiRequest(`${API_BASE_URL}/admin/desktop/releases/${id}/enable`, {
    method: 'POST',
    auth: true,
  });
};

/**
 * 禁用桌面版本（需认证，仅超管）
 * @param {number} id - 记录 ID
 * @returns {Promise<{ status: 'success', id: number }>}
 */
export const disableDesktopRelease = (id) => {
  return apiRequest(`${API_BASE_URL}/admin/desktop/releases/${id}/disable`, {
    method: 'POST',
    auth: true,
  });
};
